import React from 'react';
import PropTypes from 'prop-types';
import { LuMapPin, LuTag } from 'react-icons/lu';
import useStockText from '../stockText';
import { STOCK_COLUMNS, isEmptyValue } from '../stockColumns';
import { CATEGORY_GRADIENTS, CategoryBadge, RemainingValue, ScopeChip, StockStatusBadge, categoryKey, nameInitials } from './stockBits';

const NUMERIC = new Set(['Döviz Kuru', 'Giriş Fiyatı', 'Kalan', 'Min Miktar', 'Kar Oranı(%)', 'Miktar', 'Yıpranma Payı', 'Çıkış Fiyatı']);

/** Plain-text form of a cell, for its tooltip (the cell itself truncates). */
export const getCellTitle = (stock, key) => {
  const value = stock[key];
  if (['Durum', 'Kategori', 'ownershipType'].includes(key) || isEmptyValue(value) || typeof value === 'object') return undefined;
  return String(value);
};

/** Content of one stock table cell. */
const StockCell = ({ columnKey, stock, low }) => {
  const { ownershipLabel, formatDay } = useStockText();
  const value = stock[columnKey];
  const muted = <span className="tw-text-slate-400 dark:tw-text-slate-500">-</span>;

  switch (columnKey) {
    case 'Adı':
      return (
        <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5">
          <span aria-hidden="true" className={`tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-text-[10px] tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] ${CATEGORY_GRADIENTS[categoryKey(stock['Kategori'])]}`}>
            {nameInitials(value)}
          </span>
          <span className="tw-min-w-0 tw-truncate tw-font-semibold tw-text-slate-900 dark:tw-text-white">{isEmptyValue(value) ? '-' : String(value)}</span>
        </span>
      );
    case 'Durum':
      return <StockStatusBadge value={isEmptyValue(value) ? undefined : String(value)} />;
    case 'Kategori':
      return isEmptyValue(value) ? muted : <CategoryBadge value={String(value)} />;
    case 'customerName':
    case 'projectName':
      return <ScopeChip value={isEmptyValue(value) ? '' : String(value)} />;
    case 'ownershipType':
      // Sahiplik yalnızca demirbaşta anlamlı
      return <ScopeChip value={stock['Kategori'] === 'demirbas' ? ownershipLabel(value) : ''} />;
    case 'Kalan':
      return <RemainingValue value={value} low={low} />;
    default:
      if (isEmptyValue(value)) return muted;
      if (STOCK_COLUMNS[columnKey]?.type === 'date') return <span className="tw-block tw-truncate tw-tabular-nums">{formatDay(value)}</span>;
      return <span className={`tw-block tw-truncate ${NUMERIC.has(columnKey) ? 'tw-tabular-nums' : ''}`}>{String(value)}</span>;
  }
};

StockCell.propTypes = { columnKey: PropTypes.string.isRequired, stock: PropTypes.object.isRequired, low: PropTypes.bool };

/** One row of the card list (narrow screens): name, type, place, remaining and status. */
export const StockCardRow = ({ stock, low }) => {
  const { label } = useStockText();
  const name = isEmptyValue(stock['Adı']) ? '-' : String(stock['Adı']);
  const type = isEmptyValue(stock['Stok Tipi']) ? '' : String(stock['Stok Tipi']);
  const place = isEmptyValue(stock['Depo']) ? '' : String(stock['Depo']);
  return (
    <>
      <span aria-hidden="true" className={`tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-text-xs tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] ${CATEGORY_GRADIENTS[categoryKey(stock['Kategori'])]}`}>
        {nameInitials(name)}
      </span>
      <span className="tw-min-w-0 tw-flex-1">
        <span className="tw-flex tw-min-w-0 tw-items-center tw-justify-between tw-gap-2">
          <span className="tw-min-w-0 tw-truncate tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{name}</span>
          <StockStatusBadge value={isEmptyValue(stock['Durum']) ? undefined : String(stock['Durum'])} />
        </span>
        <span className="tw-mt-1 tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
          {!isEmptyValue(stock['Kategori']) && <CategoryBadge value={String(stock['Kategori'])} />}
          {type && <span className="tw-inline-flex tw-min-w-0 tw-items-center tw-gap-1"><LuTag aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0" /><span className="tw-truncate">{type}</span></span>}
          {place && <span className="tw-inline-flex tw-min-w-0 tw-items-center tw-gap-1"><LuMapPin aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0" /><span className="tw-truncate">{place}</span></span>}
        </span>
        <span className="tw-mt-1.5 tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
          {label('card.remaining')}
          <RemainingValue value={stock['Kalan']} low={low} />
          <span aria-hidden="true">·</span>
          {label('card.min', { min: isEmptyValue(stock['Min Miktar']) ? 1 : stock['Min Miktar'] })}
        </span>
      </span>
    </>
  );
};

StockCardRow.propTypes = { stock: PropTypes.object.isRequired, low: PropTypes.bool };

export default StockCell;
